import type { RiaPhoneKind } from '@poseidon/shared-utils/advisorUniverse';

import Button from '../Input2/Button';

export interface ClickToCallButtonPureProps {
  /**
   * Team-level calling flag. When false the affordance is hidden entirely (returns null) —
   * the feature is gated, not greyed.
   */
  enabled: boolean;
  /**
   * Whether the rep can actually place a call (a caller number is provisioned). When the team
   * flag is on but this is false, the button renders greyed/disabled with an explanatory
   * tooltip rather than disappearing.
   */
  callingConnected: boolean;
  /** Contact's phone number (the value the UI shows); empty/null disables the button. */
  phoneNumber?: string | null;
  /**
   * The number is the contact's org ("firm") number, dialed because the person has no direct
   * number of their own. Only affects the tooltip wording — the button still dials `phoneNumber`.
   */
  isFirmNumber?: boolean;
  /** What the number is when its source says (`resolveCallNumber().phoneKind`) — named in the tooltip. */
  phoneKind?: RiaPhoneKind | null;
  /**
   * Why the server would refuse this call right now (`voiceCall.getContactDialInfo` →
   * `callBlockedReason`) — every number the rep could dial from has placed today's allowance of
   * calls, or the contact's number is gone from the carrier and the rep has no other. The button
   * greys out with this as its tooltip, so the rep learns it before pressing Call rather than from
   * a failed dial. Null/absent = nothing in the way.
   */
  callBlockedReason?: string | null;
  /**
   * A call is already in progress somewhere in the app (the global dialer is occupied). The
   * button greys out with a "finish your current call first" tooltip — only one live call at a
   * time across the whole surface, since the dialer is a single app-global widget.
   */
  busy?: boolean;
  /** Summon the app-global dialer for this contact. */
  onCall: () => void;
  className?: string;
}

/**
 * Pure, presentational click-to-call *trigger* — just the green Call button. The dialer itself is
 * the separate `SoftphoneDialerPure`, mounted ONCE at the app root by the per-surface
 * `SoftphoneProvider` so a live call survives navigation. This button only decides whether a call
 * CAN be started and, when clicked, asks the provider to summon the dialer for this contact.
 *
 * No tRPC, no softphone client — the smart wrappers (dashboard `ClickToCallButton`, extension
 * `ExtensionClickToCall`) wire it to the provider's `startCall`.
 */
export function ClickToCallButtonPure({
  enabled,
  callingConnected,
  phoneNumber,
  isFirmNumber,
  phoneKind,
  callBlockedReason,
  busy,
  onCall,
  className,
}: ClickToCallButtonPureProps) {
  if (!enabled) return null;

  const hasPhone = Boolean(phoneNumber);
  // Precedence: an un-provisioned rep can't call any contact, so surface that first; then the
  // per-contact "no phone" reason; then today's call limits (they last until midnight); then the
  // transient "already on a call" guard.
  const disabledReason = !callingConnected
    ? "Calling isn't set up for your account yet"
    : !hasPhone
      ? 'No phone number on file'
      : callBlockedReason
        ? callBlockedReason
        : busy
          ? 'Finish your current call first'
          : undefined;

  // When the button can place a call, the tooltip surfaces the exact number it'll dial and what it is
  // (a cell, or the firm's line); otherwise it explains why the button is greyed.
  const kindLabel = isFirmNumber
    ? 'firm number '
    : phoneKind === 'cell'
      ? 'cell '
      : phoneKind === 'office'
        ? 'office line '
        : '';
  const tooltip = disabledReason ?? (hasPhone ? `Call ${kindLabel}${phoneNumber}` : undefined);

  return (
    <Button color="green" disabled={disabledReason != null} tooltip={tooltip} onClick={onCall} className={className}>
      Call
    </Button>
  );
}
